import { type Locale, Locales } from "@ai-admin/shared";
import type { AnswersDraft, DraftService, QuestionView } from "./types.js";

/** Лимиты как на сервере (`apps/api/src/modules/onboarding/http-sources.ts`). */
export const MAX_SOURCES = 5;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_TOTAL_BYTES = 20 * 1024 * 1024;
/** Услуг в черновике столько же, сколько на сервере (`commitServices`, `saveDraft`). */
export const MAX_SERVICES = 100;
export const MAX_VOICE_SECONDS = 180;
/** Файлы, которые принимает разбор: фото, PDF, Word и Excel нового формата. */
export const FILE_ACCEPT =
  "image/*,.pdf,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export type SourceKind = "photo" | "pdf" | "docx" | "xlsx" | "voice" | "text";

/** Выбранный владельцем источник: живёт только в памяти страницы до отправки. */
export type PickedSource =
  | { id: number; kind: Exclude<SourceKind, "text" | "voice">; name: string; blob: Blob }
  | { id: number; kind: "voice"; seconds: number; blob: Blob }
  | { id: number; kind: "text"; text: string };

/** Источник до получения id (Omit по каждому варианту объединения). */
export type NewSource = PickedSource extends infer S ? (S extends PickedSource ? Omit<S, "id"> : never) : never;

export function kindOfFile(file: { type: string; name: string }): Exclude<SourceKind, "voice" | "text"> | "old" | null {
  const type = file.type.toLowerCase();
  const ext = /\.([a-z0-9]+)$/i.exec(file.name)?.[1]?.toLowerCase() ?? "";
  if (type.startsWith("image/")) return "photo";
  if (type === "application/pdf" || ext === "pdf") return "pdf";
  if (ext === "docx" || type.includes("wordprocessingml")) return "docx";
  if (ext === "xlsx" || type.includes("spreadsheetml")) return "xlsx";
  if (ext === "doc" || ext === "xls" || type === "application/msword" || type === "application/vnd.ms-excel") return "old";
  return null;
}

/** Формат записи голоса: Chrome и Firefox пишут webm/opus, Safari на iPhone только mp4. */
export function pickAudioMime(isTypeSupported: (t: string) => boolean): string | undefined {
  return ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((t) => isTypeSupported(t));
}

export const formatSeconds = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export class SourcesError extends Error {
  constructor(
    readonly reason: string,
    readonly status: number,
  ) {
    super(reason);
  }
}

/** Причина отказа сервера → ключ перевода `onb.sources.err.*` (незнакомая даёт общий текст). */
export function errorKey(reason: string): string {
  const known = [
    "too_many_sources",
    "file_too_big",
    "total_too_big",
    "unsupported_type",
    "old_format",
    "bad_file",
    "text_too_long",
    "too_big",
    "too_long",
    "empty",
    "voice_failed",
    "voice_unclear",
    "too_many",
    "busy",
    "voice_too_long",
    "source_rejected",
    "upload_timeout",
    "cap",
    "photo",
  ];
  return `onb.sources.err.${known.includes(reason) ? reason : "failed"}`;
}

/**
 * Текущий черновик шага услуг, который уходит вместе с источниками: модель дополняет его (ответы возвращаются
 * объединёнными), а не разбирает каждый следующий источник с чистого листа. Формат поля `draft` на сервере:
 * `apps/api/src/modules/onboarding/http-sources.ts` (`ParseDraftSchema`).
 */
export interface ParseDraft {
  services: Pick<DraftService, "name" | "priceRsd" | "durationMin">[];
  answers: AnswersDraft;
}

export const EMPTY_DRAFT: ParseDraft = { services: [], answers: {} };

/**
 * Отправка источников на разбор: multipart, cookie-сессия; файлы уходят один раз и на сервере не хранятся.
 * Черновик (если он не пуст) идёт полем `draft` как JSON.
 */
export async function uploadSources(
  salonId: string,
  sources: PickedSource[],
  draft: ParseDraft = EMPTY_DRAFT,
  fetchFn: typeof fetch = fetch,
): Promise<{ services: DraftService[]; answers: AnswersDraft }> {
  const form = new FormData();
  for (const s of sources) {
    if (s.kind === "text") form.append("text", s.text);
    else if (s.kind === "voice") form.append("file", s.blob, s.blob.type.includes("mp4") ? "voice.mp4" : "voice.webm");
    else form.append("file", s.blob, s.name);
  }
  if (draft.services.length > 0 || Object.keys(draft.answers).length > 0) {
    form.append(
      "draft",
      JSON.stringify({
        services: draft.services.map((s) => ({ name: s.name, priceRsd: s.priceRsd, durationMin: s.durationMin })),
        answers: draft.answers,
      }),
    );
  }
  let res: Response;
  try {
    res = await fetchFn(`/api/onboarding/${salonId}/sources`, { method: "POST", body: form, credentials: "same-origin" });
  } catch {
    throw new SourcesError("network", 0);
  }
  const body = (await res.json().catch(() => null)) as { services?: DraftService[]; answers?: AnswersDraft; error?: { code?: string; reason?: string } } | null;
  if (!res.ok || !body?.services) {
    const code = body?.error?.code;
    throw new SourcesError(code === "cap_exceeded" ? "cap" : (body?.error?.reason ?? "failed"), res.status);
  }
  return { services: body.services, answers: body.answers ?? {} };
}

/**
 * Ответ в форме редактирования: текст, выбор и число как строка, языки как список, «темы» как строки через перевод строки.
 * Так одно поле (`QuestionField`) правит и черновик из источников, и шаг правил.
 */
export type FieldValue = string | Locale[];
export type AnswerForm = Partial<Record<string, FieldValue>>;

export function toForm(draft: AnswersDraft): AnswerForm {
  const out: AnswerForm = {};
  for (const [id, v] of Object.entries(draft)) {
    if (v === undefined || v === null) continue;
    if (id === "languages") out[id] = (v as Locale[]).filter((x) => (Locales as readonly string[]).includes(x));
    else if (Array.isArray(v)) out[id] = v.join("\n");
    else out[id] = String(v);
  }
  return out;
}

/** Пределы чисел как на шаге правил. */
export const NUMBER_LIMITS: Record<string, [number, number]> = { cancel_hours: [0, 168], late_tolerance: [0, 120] };

export function parseNumber(s: string, [min, max]: [number, number]): number | null {
  if (!/^\d+$/.test(s.trim())) return null;
  const n = Number(s);
  return n >= min && n <= max ? n : null;
}

/** Форма → черновик для сервера: пустые поля выпадают; `null` означает, что в форме есть неверное число. */
export function fromForm(form: AnswerForm, questions: readonly QuestionView[]): AnswersDraft | null {
  const out: Record<string, unknown> = {};
  for (const q of questions) {
    const v = form[q.id];
    if (v === undefined) continue;
    if (q.kind === "languages") {
      if (Array.isArray(v) && v.length > 0) out[q.id] = v;
      continue;
    }
    const s = (typeof v === "string" ? v : "").trim();
    if (!s) continue;
    if (q.kind === "number") {
      const n = parseNumber(s, NUMBER_LIMITS[q.id] ?? [0, 10_000]);
      if (n === null) return null;
      out[q.id] = n;
    } else if (q.kind === "list") {
      const items = s
        .split("\n")
        .map((x) => x.trim().slice(0, 200))
        .filter(Boolean)
        .slice(0, 50);
      if (items.length > 0) out[q.id] = items;
    } else if (q.kind === "choice") {
      if (q.options.includes(s)) out[q.id] = s;
    } else out[q.id] = s;
  }
  return out as AnswersDraft;
}
